import { otherColors } from '@ehrTheme/colors';
import {
  Autocomplete,
  Box,
  Card,
  CircularProgress,
  debounce,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ErxSearchAllergensResponse } from '@oystehr/sdk';
import { enqueueSnackbar } from 'notistack';
import { FC, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RoundedButton } from 'src/components/RoundedButton';
import { dataTestIds } from 'src/constants/data-test-ids';
import { sortByRecencyAndStatus } from 'src/helpers';
import { AllergyDTO } from 'utils';
import { DeleteIconButton } from '../../../../../components/DeleteIconButton';
import { useChartDataArrayValue } from '../../hooks/useChartDataArrayValue';
import { useGetAppointmentAccessibility } from '../../hooks/useGetAppointmentAccessibility';
import { ExtractObjectType, useGetAllergiesSearch } from '../../stores/appointment/appointment.queries';
import {
  ChartDataState,
  useChartData,
  useDeleteChartData,
  useSaveChartData,
} from '../../stores/appointment/appointment.store';
import { useAppFlags } from '../../stores/contexts/useAppFlags';
import { ProviderSideListSkeleton } from '../ProviderSideListSkeleton';

export const KnownAllergiesProviderColumn: FC = () => {
  const { t } = useTranslation();
  const { chartData, isLoading: isChartDataLoading } = useChartData();
  const { isAppointmentReadOnly: isReadOnly } = useGetAppointmentAccessibility();
  const featureFlags = useAppFlags();
  const allergies = sortByRecencyAndStatus(chartData?.allergies ?? []);
  const length = allergies.length;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      data-testid={dataTestIds.allergies.knownAllergiesColumn}
    >
      {isChartDataLoading && <ProviderSideListSkeleton />}

      {length > 0 && !isChartDataLoading && (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
          data-testid={dataTestIds.allergies.knownAllergiesList}
        >
          {allergies.map((value, index) => (
            <AllergyListItem key={value.resourceId || `new${index}`} value={value} index={index} length={length} />
          ))}
        </Box>
      )}

      {allergies.length === 0 && isReadOnly && !isChartDataLoading && !featureFlags.isInPerson && (
        <Typography color="secondary.light">{t('telemed.chart.reconcileMissing')}</Typography>
      )}

      {!isReadOnly && <AddAllergyField />}
    </Box>
  );
};

const setUpdatedAllergy = (
  chartDataSetState: (updater: Partial<ChartDataState> | ((state: ChartDataState) => Partial<ChartDataState>)) => void,
  updatedAllergy?: AllergyDTO
): void => {
  if (updatedAllergy) {
    // todo: check is valid
    chartDataSetState((prevState) => ({
      chartData: {
        ...prevState.chartData!,
        allergies: prevState.chartData?.allergies?.map((allergy) =>
          allergy.resourceId === updatedAllergy.resourceId ? updatedAllergy : allergy
        ),
      },
    }));
  }
};

const AllergyListItem: FC<{ value: AllergyDTO; index: number; length: number }> = ({ value, index, length }) => {
  const { t } = useTranslation();
  const [note, setNote] = useState(value.note || '');
  const areNotesEqual = note.trim() === (value.note || '');
  const featureFlags = useAppFlags();
  const { chartDataSetState } = useChartData();
  const { isAppointmentReadOnly: isReadOnly } = useGetAppointmentAccessibility();
  const { mutate: updateChartData, isPending: isUpdateLoading } = useSaveChartData();
  const { mutate: deleteChartData, isPending: isDeleteLoading } = useDeleteChartData();
  const isLoading = isUpdateLoading || isDeleteLoading;
  const isLoadingOrAwaiting = isLoading || !areNotesEqual;
  const isAlreadySaved = !!value.resourceId;

  const updateNote = useMemo(
    () =>
      debounce((input: string) => {
        updateChartData(
          {
            allergies: [{ ...value, note: input.trim() || undefined }],
          },
          {
            onSuccess: (data) => {
              const updatedAllergy = data.chartData.allergies?.[0];
              setUpdatedAllergy(chartDataSetState, updatedAllergy);
            },
            onError: () => {
              enqueueSnackbar(t('telemed.chart.errorUpdateAllergyNote'), {
                variant: 'error',
              });
            },
          }
        );
      }, 1500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value.current, t]
  );

  const updateCurrent = (newCurrentValue: boolean): void => {
    updateChartData(
      {
        allergies: [{ ...value, current: newCurrentValue, note: newCurrentValue ? undefined : value.note }],
      },
      {
        onSuccess: (data) => {
          if (newCurrentValue) {
            setNote('');
          }
          const updatedAllergy = data.chartData.allergies?.[0];
          setUpdatedAllergy(chartDataSetState, updatedAllergy);
        },
        onError: () => {
          enqueueSnackbar(t('telemed.chart.errorUpdateAllergyStatus'), {
            variant: 'error',
          });
        },
      }
    );
  };

  const deleteAllergy = (): void => {
    deleteChartData(
      {
        allergies: [value],
      },
      {
        onSuccess: () => {
          chartDataSetState((prevState) => ({
            chartData: {
              ...prevState.chartData!,
              allergies: prevState.chartData?.allergies?.filter((allergy) => allergy.resourceId !== value.resourceId),
            },
          }));
        },
        onError: () => {
          enqueueSnackbar(t('telemed.chart.errorDeleteAllergy'), {
            variant: 'error',
          });
        },
      }
    );
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
      data-testid={dataTestIds.allergies.knownAllergiesListItem}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          sx={{
            color: (theme) => (!value.current && featureFlags.isInPerson ? theme.palette.text.secondary : undefined),
          }}
        >
          {value.name}
          {featureFlags.isInPerson &&
            isReadOnly &&
            ` | ${value.current ? t('telemed.chart.current') : t('telemed.chart.inactiveNow')}${
              value.note ? ' | ' + t('telemed.chart.providerNotes') + ': ' + value.note : ''
            }`}
        </Typography>

        {!isReadOnly && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {featureFlags.isInPerson && (
              <FormControlLabel
                control={<Switch checked={value.current} onChange={(e) => updateCurrent(e.target.checked)} />}
                label={value.current ? t('telemed.chart.current') : t('telemed.chart.inactiveNow')}
                disabled={isLoadingOrAwaiting || !isAlreadySaved}
                labelPlacement="start"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    marginRight: 1,
                    textAlign: 'right',
                    color: (theme) => (!value.current ? theme.palette.text.secondary : undefined),
                  },
                }}
              />
            )}
            <DeleteIconButton disabled={isLoadingOrAwaiting || !isAlreadySaved} onClick={deleteAllergy} />
          </Box>
        )}
      </Box>

      {!value.current && !isReadOnly && featureFlags.isInPerson && (
        <TextField
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            updateNote(e.target.value);
          }}
          disabled={(isLoading && areNotesEqual) || !isAlreadySaved}
          size="small"
          fullWidth
          label={t('telemed.chart.notesForInactiveAllergy')}
          InputProps={{
            endAdornment: !areNotesEqual && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size="20px" />
              </Box>
            ),
          }}
        />
      )}

      {index + 1 !== length && <Divider />}
    </Box>
  );
};

const AddAllergyField: FC = () => {
  const { t } = useTranslation();
  const { isChartDataLoading } = useChartData();
  const { onSubmit, isLoading } = useChartDataArrayValue('allergies');

  const methods = useForm<{ value: ExtractObjectType<ErxSearchAllergensResponse> | null; otherAllergyName: string }>({
    defaultValues: { value: null, otherAllergyName: '' },
  });

  const { control, reset, handleSubmit } = methods;
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isOtherOptionSelected, setIsOtherOptionSelected] = useState(false);
  const { isFetching: isSearching, data } = useGetAllergiesSearch(debouncedSearchTerm);

  const allergiesSearchOptions = useMemo(() => {
    if (!data || isSearching) return [];

    // Process the data to include brand name
    const allergiesWithBrand = data.map((allergy) => {
      const brandName = allergy.brandName;
      if (brandName && brandName !== allergy.name) {
        return {
          ...allergy,
          name: `${allergy.name} (${brandName})`,
        };
      }
      return allergy;
    });

    return [
      ...allergiesWithBrand,
      { name: t('telemed.chart.procedures.other') } as unknown as ExtractObjectType<ErxSearchAllergensResponse>,
    ];
  }, [data, isSearching, t]);

  const debouncedHandleInputChange = useMemo(
    () =>
      debounce((data) => {
        if (data.length > 2) {
          setDebouncedSearchTerm(data);
        }
      }, 800),
    []
  );

  const handleSelectOption = async (data: ExtractObjectType<ErxSearchAllergensResponse> | null): Promise<void> => {
    if (data) {
      const newValue = {
        name: data.name,
        id: data.id?.toString(),
        current: true,
      };

      try {
        await onSubmit(newValue);
        reset({ value: null, otherAllergyName: '' });
        setIsOtherOptionSelected(false);
      } catch {
        // Error is already handled by useChartDataArrayValue
      }
    }
  };

  const onSubmitForm = async (data: {
    value: ExtractObjectType<ErxSearchAllergensResponse> | null;
    otherAllergyName: string;
  }): Promise<void> => {
    if (data.value) {
      const allergyData = {
        ...data.value,
        name: t('telemed.chart.procedures.other') + (data.otherAllergyName ? ` (${data.otherAllergyName})` : ''),
      };
      await handleSelectOption(allergyData);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <Card
        elevation={0}
        sx={{
          p: 3,
          backgroundColor: otherColors.formCardBg,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Controller
          name="value"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <Autocomplete
              value={value || null}
              onChange={(_e, data) => {
                onChange((data || '') as any);
                if (data?.name === t('telemed.chart.procedures.other')) {
                  setIsOtherOptionSelected(true);
                } else {
                  setIsOtherOptionSelected(false);
                  void handleSelectOption(data);
                }
              }}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.name || '')}
              fullWidth
              size="small"
              loading={isSearching}
              filterOptions={(options) => options}
              isOptionEqualToValue={(option, value) => option.name === value.name}
              disablePortal
              blurOnSelect
              disabled={isChartDataLoading || isLoading}
              options={allergiesSearchOptions}
              noOptionsText={
                debouncedSearchTerm && debouncedSearchTerm.length > 2 && allergiesSearchOptions.length === 0
                  ? t('telemed.chart.nothingFound')
                  : t('telemed.chart.startTyping')
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  onChange={(e) => debouncedHandleInputChange(e.target.value)}
                  data-testid={dataTestIds.allergies.knownAllergiesInput}
                  label={t('telemed.chart.agentSubstance')}
                  placeholder={t('telemed.chart.searchPlaceholder')}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      fontWeight: 'bold',
                    },
                  }}
                />
              )}
            />
          )}
        />
        {isOtherOptionSelected && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Controller
              name="otherAllergyName"
              control={control}
              render={({ field: { value, onChange } }) => (
                <TextField
                  value={value || ''}
                  onChange={(e) => onChange(e.target.value)}
                  label={t('telemed.chart.otherAllergy')}
                  placeholder={t('telemed.chart.pleaseSpecify')}
                  fullWidth
                  size="small"
                />
              )}
            />
            <RoundedButton type="submit" disabled={isLoading}>
              {t('telemed.vitals.add')}
            </RoundedButton>
          </Stack>
        )}
      </Card>
    </form>
  );
};
